import { useEffect, useState } from "react";
import { fetchResearchSummary, type ResearchSummaryDto } from "../api";
import { PageHelper } from "../components/PageHelper";

const dash = "—";
const pct = (v: number | null | undefined) => (v == null ? dash : `${(v * 100).toFixed(1)}%`);
const frac = (a: number | null | undefined, b: number | null | undefined) => (a == null || b == null ? dash : `${a} / ${b}`);

const TONE = {
  rej: { bg: "#fceceb", border: "#f3cfcb", icon: "○", tagColor: "#df453b" },
  bless: { bg: "#e4f5ee", border: "#c9ebda", icon: "◆", tagColor: "#0e9f6e" },
} as const;

function Chips({ items }: { items: string[] }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 14, alignItems: "center" }}>
      <span className="note" style={{ textTransform: "uppercase", letterSpacing: "0.07em", marginRight: 4 }}>Method</span>
      {items.map((c) => (
        <span key={c} className="chip" style={{ fontFamily: "var(--font-mono)" }}>▸ {c}</span>
      ))}
    </div>
  );
}

function Box({ kind, title, children }: { kind: "trust" | "cav"; title: string; children: React.ReactNode }) {
  const style =
    kind === "trust"
      ? { background: "#e4f5ee", border: "1px solid #c9ebda", color: "#0e9f6e" }
      : { background: "#f8f0dd", border: "1px solid #ecdcc6", color: "#cf9421" };
  return (
    <div style={{ ...style, borderRadius: 7, padding: "11px 14px", fontSize: "0.82rem", lineHeight: 1.5, flex: 1, minWidth: 260 }}>
      <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: "0.62rem", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 5 }}>{title}</div>
      <div style={{ color: "var(--ink-soft)" }}>{children}</div>
    </div>
  );
}

function Finding({
  tone, title, body, tag, chips, stats, trust, caveat, source,
}: {
  tone: "rej" | "bless";
  title: string;
  body: string;
  tag: string;
  chips: string[];
  stats: { k: string; v: string }[];
  trust: React.ReactNode;
  caveat: React.ReactNode;
  source: string;
}) {
  const t = TONE[tone];
  return (
    <div style={{ background: "#fff", border: "1px solid var(--line2)", borderRadius: 8, marginBottom: 16, overflow: "hidden" }}>
      <div style={{ background: t.bg, borderBottom: `1px solid ${t.border}`, padding: "16px 18px", display: "flex", gap: 16, alignItems: "flex-start" }}>
        <div style={{ width: 44, height: 44, borderRadius: "50%", background: "#fff", display: "grid", placeItems: "center", fontSize: "1.3rem", flex: "none", color: t.tagColor }}>{t.icon}</div>
        <div style={{ flex: 1 }}>
          <div className="note" style={{ textTransform: "uppercase", letterSpacing: "0.08em" }}>Claim tested</div>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "1.08rem" }}>{title}</div>
          <div style={{ fontSize: "0.86rem", color: "var(--ink-soft)", marginTop: 4 }}>{body}</div>
        </div>
        <span style={{ fontFamily: "var(--font-mono)", fontSize: "0.74rem", fontWeight: 600, color: t.tagColor, background: "#fff", border: `1px solid ${t.border}`, borderRadius: 6, padding: "5px 10px", whiteSpace: "nowrap" }}>{tag}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${stats.length}, 1fr)`, gap: 8, padding: "14px 18px", borderBottom: "1px solid var(--line)", background: "#fafbfc" }}>
        {stats.map((s) => (
          <div key={s.k} className="stt">
            <div className="k">{s.k}</div>
            <div className="v">{s.v}</div>
          </div>
        ))}
      </div>
      <div style={{ padding: "16px 18px" }}>
        <Chips items={chips} />
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {trust}
          {caveat}
        </div>
        <div className="note" style={{ marginTop: 12, fontStyle: "italic" }}>Source: {source}</div>
      </div>
    </div>
  );
}

export function ResearchLogPage({ token, onUnauthorized }: { token: string; onUnauthorized: () => void }) {
  const [data, setData] = useState<ResearchSummaryDto | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchResearchSummary(token)
      .then(setData)
      .catch((err) => {
        if (String(err).includes("401") || String(err).includes("403")) onUnauthorized();
        setError(String(err));
      });
  }, [token, onUnauthorized]);

  const files = data ? Object.entries(data.meta) : [];
  const problems = files.filter(([, m]) => !m.present || m.stale);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Research Log</h1>
          <div className="page-sub">What we tested, and what survived. Every claim ran through out-of-sample validation with placebo controls — the honest nulls are why a surviving signal is worth trusting.</div>
        </div>
      </div>

      <PageHelper>
        Each card is a claim we tested, the verdict, and the numbers from the run that produced it. Nulls are
        shown alongside signals on purpose: a test that correctly rejects fake data is the reason its rejections of
        real data can be believed.
      </PageHelper>

      {error && <div className="banner show"><span>⚠</span><span>Could not load research results: {error}</span></div>}
      {problems.length > 0 && (
        <div className="banner show" style={{ display: "flex" }}>
          <span>⚠</span>
          <span>
            Results need attention:{" "}
            {problems.map(([, m]) => `${m.file} (${!m.present ? "missing" : "stale"})`).join(", ")}. Figures from those
            files may be out of date.
          </span>
        </div>
      )}
      {data && (
        <div className="note" style={{ marginBottom: 12 }}>
          Results as of{" "}
          {files.map(([name, m]) => `${name} ${m.as_of ? m.as_of.slice(0, 10) : dash}`).join(" · ")}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 12, margin: "18px 0 8px" }}>
        {[
          { k: "New data (macro · commodity · FPI)", v: frac(data?.phase2?.lead_relevant, data?.phase2?.tests), d: "lead-relevant to sector returns OOS", c: "var(--down)" },
          { k: "Blessing gate", v: frac(data?.blessing_gate?.ship, data?.blessing_gate?.pairs), d: "pairs that ship over current models", c: "var(--down)" },
          { k: "Stock-family tests clearing FDR", v: frac(data?.stock?.fdr_relevant, data?.stock?.tests), d: `${data?.stock?.blessed_relevant ?? dash} of them in blessed segment tests`, c: "var(--up)" },
          { k: "New-data placebo · random walk", v: pct(data?.phase2?.placebo_random_walk), d: "fire-rate — test runs conservative", c: "var(--ink)" },
        ].map((card) => (
          <div key={card.k} className="sc">
            <div className="k">{card.k}</div>
            <div className="v" style={{ color: card.c }}>{card.v}</div>
            <div className="d">{card.d}</div>
          </div>
        ))}
      </div>

      <div className="sechead" style={{ fontFamily: "var(--font-display)", fontWeight: 800, margin: "24px 0 10px" }}>
        NULLS <span className="note">claims we tested and rejected — the discipline that makes the rest credible</span>
      </div>

      <Finding
        tone="rej"
        tag="✕ NO SIGNAL"
        title="Macro, commodity & FPI flows predict segment returns"
        body={`They don't — not out of sample. ${data?.phase2?.tests ?? dash} feature × segment tests, none lead-relevant after calibrated testing. ${data?.phase2?.unstable ?? dash} looked significant in-sample but did not hold in the rolling-origin windows.`}
        chips={["time-series lead-lag", "stationary block bootstrap", "rolling-origin re-test", "placebo: white-noise + random-walk", "BH-FDR · new-data family"]}
        stats={[
          { k: "Survived OOS", v: frac(data?.phase2?.lead_relevant, data?.phase2?.tests) },
          { k: "In-sample only (unstable)", v: data?.phase2 ? String(data.phase2.unstable) : dash },
          { k: "White-noise placebo", v: pct(data?.phase2?.placebo_white_noise) },
        ]}
        trust={
          <Box kind="trust" title="⛉ Why trustworthy">
            The first cut over-fired on placebos: Newey-West under-corrected random-walk persistence. Fixed with a <b>stationary block bootstrap</b>, then a <b>rolling-origin</b> re-test. Final calibration: white-noise {pct(data?.phase2?.placebo_white_noise)}, random-walk {pct(data?.phase2?.placebo_random_walk)}. A test that correctly rejects fake data is one you can believe when it rejects real data.
          </Box>
        }
        caveat={
          <Box kind="cav" title="⚠ Honest caveat">
            Conservative calibration costs power: at {pct(data?.phase2?.placebo_random_walk)} the test could <b>miss a genuinely weak signal</b>. Honest reading: "no reliable, exploitable signal at this horizon", not "impossible". No retraining is warranted on this basis.
          </Box>
        }
        source="feature_search_phase2_rolling.json"
      />

      <div className="sechead" style={{ fontFamily: "var(--font-display)", fontWeight: 800, margin: "20px 0 10px" }}>
        DOCUMENTED — NOT YET RE-PERSISTED <span className="note">tested in earlier work; no results file yet, so no numbers are shown</span>
      </div>
      <div style={{ background: "#fff", border: "1px dashed var(--line2)", borderRadius: 8, padding: "14px 16px", marginBottom: 12, display: "flex", gap: 14, alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>Index direction can be forecast — rejected</div>
          <div className="note" style={{ marginTop: 3 }}>Multiple earlier modelling approaches found index direction non-forecastable. Queued to recompute so it can become a full evidence card.</div>
        </div>
        <span className="chip" style={{ whiteSpace: "nowrap", color: "#cf9421" }}>not persisted</span>
      </div>
      <div style={{ background: "#fff", border: "1px dashed var(--line2)", borderRadius: 8, padding: "14px 16px", marginBottom: 12, display: "flex", gap: 14, alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>FPI sector-rotation is cyclical &amp; exploitable — rejected</div>
          <div className="note" style={{ marginTop: 3 }}>No lead-lag cyclicity found in an earlier test. Queued to recompute so it can become a full evidence card.</div>
        </div>
        <span className="chip" style={{ whiteSpace: "nowrap", color: "#cf9421" }}>not persisted</span>
      </div>

      <div className="sechead" style={{ fontFamily: "var(--font-display)", fontWeight: 800, margin: "24px 0 10px" }}>
        SIGNAL <span className="note">what survived the same bar — theory-consistent, out of sample, placebo-corrected</span>
      </div>

      <Finding
        tone="bless"
        tag="✓ SIGNAL"
        title="Per-segment stock features carry a real signal"
        body={`${frac(data?.stock?.blessed_relevant, data?.stock?.blessed_tests)} blessed feature × segment tests clear the stock-family FDR bar; ${frac(data?.stock?.fdr_relevant, data?.stock?.tests)} of all stock tests do, and ${frac(data?.stock?.placebo_relevant, data?.stock?.placebo_tests)} placebos cleared. The signal concentrates in short-horizon reversal and volatility.`}
        chips={["walk-forward OOS rank-IC", "BH-FDR · stock family", "non-overlapping effective-N", "correlation-cluster de-redundancy", "incremental-value-conditional"]}
        stats={[
          { k: "Stock tests clearing FDR", v: frac(data?.stock?.fdr_relevant, data?.stock?.tests) },
          { k: "Blessed feature × segment tests", v: frac(data?.stock?.blessed_relevant, data?.stock?.blessed_tests) },
          { k: "Blessing-gate pairs that ship", v: frac(data?.blessing_gate?.ship, data?.blessing_gate?.pairs) },
        ]}
        trust={
          <Box kind="trust" title="⛉ Why trustworthy">
            {data?.distinct_effects ? `${data.distinct_effects.raw_clears} raw clears collapse to ${data.distinct_effects.distinct_effects} distinct effects. ` : ""}
            Of {data?.distinct_effects?.incremental_tests ?? dash} incremental candidates, {data?.distinct_effects?.distinct_adds_value ?? dash} are distinct effects that add value over the models' existing features. The surviving signal sits where micro-structure says it should.
          </Box>
        }
        caveat={
          <Box kind="cav" title="⚠ Honest caveat">
            Not every segment carries signal. Those that don't are flagged rather than shipped with a weak guess, and strength varies by segment, so the conviction score shows how far to trust each one.
          </Box>
        }
        source="feature_search_phase1.json · feature_search_phase1b.json · feature_blessing_gate.json"
      />

      <div className="card" style={{ marginTop: 22, background: "#ecebfb", border: "1px solid #c9c8f2" }}>
        <div className="note" style={{ textTransform: "uppercase", letterSpacing: "0.08em", color: "#3b3ad4" }}>Where this goes next · V2</div>
        <div style={{ fontSize: "0.86rem", color: "var(--ink-soft)" }}>
          This admin log is the private proof. The public version becomes <b>“Market Myths: Tested”</b> — the same cards written for readers: we checked, here's the evidence, here's what we won't pretend to know.
        </div>
      </div>
    </>
  );
}
